import React, { useState, useEffect } from 'react';
import { useProducts } from '../context/ProductContext';
import { Product, ProductSEO } from '../data/products';
import { ASSET_IMAGES } from '../assets/images';
import { SafeImage } from '../components/SafeImage';
import { formatKsh } from '../utils/formatters';
import {
  Boxes,
  Inbox,
  Sparkles,
  Globe,
  BarChart3,
  Settings,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  RefreshCw,
  Check,
  X,
  ExternalLink,
  ChevronRight,
  Phone,
  MessageSquare,
  Download,
  Tag,
  Filter,
  SlidersHorizontal,
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Menu,
  DollarSign,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  FileText,
  Smartphone,
  Monitor,
  Building2,
  ShieldCheck,
  TrendingUp,
  Cpu,
  Layers,
  HelpCircle,
  Database,
  Image as ImageIcon,
  LogOut
} from 'lucide-react';
import { AdminBlogManager } from '../components/admin/AdminBlogManager';
import { AdminDatabaseSchema } from '../components/admin/AdminDatabaseSchema';
import { SiteImagesManager } from '../components/admin/SiteImagesManager';
import { ImageSelectorField } from '../components/admin/ImageSelectorField';
import { performAdminLogout } from './AdminLoginPage';

interface AdminDashboardPageProps {
  onNavigate: (page: string, productId?: string) => void;
}

type AdminTab = 'inventory' | 'blogs' | 'inquiries' | 'ai-studio' | 'schema' | 'seo' | 'analytics' | 'settings' | 'images';

export interface LeadInquiry {
  id: string;
  customerName: string;
  businessName: string;
  phone: string;
  email: string;
  county: string;
  notes: string;
  items: { name: string; quantity: number; price: number }[];
  totalKsh: number;
  date: string;
  status: 'New' | 'Contacted' | 'Invoiced' | 'Closed';
}

const DEFAULT_IMAGES = [
  { label: 'Water RO Plant', url: ASSET_IMAGES.heroWater, category: 'water-purification' },
  { label: 'Milk ATM Dispenser', url: ASSET_IMAGES.milkAtm, category: 'milk-atm' },
  { label: 'Water Vending Station', url: ASSET_IMAGES.waterStation, category: 'water-vending' },
  { label: 'Milk Pasteurizer Tank', url: ASSET_IMAGES.milkPasteurizer, category: 'pasteurizers' },
  { label: 'Cooking Oil ATM', url: ASSET_IMAGES.cookingOil, category: 'cooking-oil' }
];

const INITIAL_SAMPLE_LEADS: LeadInquiry[] = [
  {
    id: 'RFQ-894102',
    customerName: 'John Mwangi',
    businessName: 'Nakuru Modern Dairies',
    phone: '+254 712 345 678',
    email: 'j.mwangi@nakurudairies.co.ke',
    county: 'Nakuru',
    notes: 'Urgent requirement for milk pasteurizer and twin ATM for retail depot along Nakuru-Eldoret highway.',
    items: [
      { name: '300 Liters 3-Phase Commercial Milk Pasteurizer', quantity: 1, price: 340000 },
      { name: '200 Litres Digital Twin-Dispense Milk ATM', quantity: 1, price: 175000 }
    ],
    totalKsh: 515000,
    date: '14 Oct 2026',
    status: 'Invoiced'
  },
  {
    id: 'RFQ-893450',
    customerName: 'Beatrice Chebet',
    businessName: 'Oasis Pure Spring Kiosk',
    phone: '+254 722 890 123',
    email: 'bchebet@gmail.com',
    county: 'Eldoret',
    notes: 'Planning commercial water refilling shop near Moi University. Need installation and water testing.',
    items: [
      { name: '1000 Litres/Hour Commercial Reverse Osmosis Water Purification Plant', quantity: 1, price: 285000 },
      { name: '4-Tap Commercial Bottled Water Vending Station', quantity: 1, price: 145000 }
    ],
    totalKsh: 430000,
    date: '12 Oct 2026',
    status: 'New'
  },
  {
    id: 'RFQ-892184',
    customerName: 'Peter Omondi',
    businessName: 'Lake Basin Supermarket',
    phone: '+254 733 456 789',
    email: 'omondi.peter@lakebasin.co.ke',
    county: 'Kisumu',
    notes: 'Requires automated 3-nozzle cooking oil ATM with GSM sales tracking SMS feature for customer self-service.',
    items: [
      { name: '3-Nozzle Automated Salad Cooking Oil ATM Dispenser', quantity: 1, price: 165000 }
    ],
    totalKsh: 165000,
    date: '10 Oct 2026',
    status: 'Contacted'
  }
];

export const AdminDashboardPage: React.FC<AdminDashboardPageProps> = ({ onNavigate }) => {
  const { products, addProduct, updateProduct, deleteProduct, resetToDefaultProducts } = useProducts();

  // Navigation & View state
  const [activeTab, setActiveTab] = useState<AdminTab>('inventory');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Search & Filters for Inventory
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState<'all' | 'inStock' | 'outOfStock'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'price-asc' | 'price-desc'>('name');

  // Leads & Inquiries state (Database backed)
  const [leads, setLeads] = useState<LeadInquiry[]>(INITIAL_SAMPLE_LEADS);
  const [leadsFilter, setLeadsFilter] = useState<'all' | 'New' | 'Contacted' | 'Invoiced' | 'Closed'>('all');
  const [selectedLead, setSelectedLead] = useState<LeadInquiry | null>(null);

  // Form State for Products
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalTab, setModalTab] = useState<'details' | 'specs' | 'seo'>('details');

  const [formData, setFormData] = useState<{
    name: string;
    category: Product['category'];
    priceKsh: number;
    originalPriceKsh?: number;
    inStock: boolean;
    image: string;
    description: string;
    tagline: string;
    capacity: string;
    material: string;
    power: string;
    features: string[];
    specs: { label: string; value: string }[];
    seo: ProductSEO;
  }>({
    name: '',
    category: 'water-purification',
    priceKsh: 85000,
    originalPriceKsh: undefined,
    inStock: true,
    image: DEFAULT_IMAGES[0].url,
    description: '',
    tagline: '',
    capacity: '100 Litres',
    material: 'Food-Grade Stainless Steel AISI 304',
    power: '220V 50Hz',
    features: [''],
    specs: [{ label: '', value: '' }],
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: '',
      tags: []
    }
  });

  // Dedicated AI Studio Tab State
  const [aiStudioPrompt, setAiStudioPrompt] = useState('');
  const [aiStudioCategory, setAiStudioCategory] = useState<Product['category']>('water-purification');
  const [aiStudioCapacity, setAiStudioCapacity] = useState('500 Liters / Hour');
  const [aiStudioPrice, setAiStudioPrice] = useState<number>(150000);
  const [isGeneratingStudioAi, setIsGeneratingStudioAi] = useState(false);
  const [generatedDraftProduct, setGeneratedDraftProduct] = useState<any | null>(null);
  const [studioSuccessNotice, setStudioSuccessNotice] = useState<string | null>(null);

  // Modal AI helper
  const [modalAiPrompt, setModalAiPrompt] = useState('');
  const [isGeneratingModalAi, setIsGeneratingModalAi] = useState(false);
  const [modalAiMessage, setModalAiMessage] = useState<string | null>(null);

  // Confirmation Modals
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [resetCatalogConfirm, setResetCatalogConfirm] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // SEO Tab state
  const [selectedSeoProductId, setSelectedSeoProductId] = useState<string>(products[0]?.id || '');
  const [seoPreviewDevice, setSeoPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');

  // ROI Simulator in Analytics
  const [roiMachineType, setRoiMachineType] = useState<'water' | 'milk' | 'oil'>('milk');
  const [roiDailyVolume, setRoiDailyVolume] = useState<number>(300);
  const [roiCostPerLitre, setRoiCostPerLitre] = useState<number>(55);
  const [roiSalePerLitre, setRoiSalePerLitre] = useState<number>(75);

  // Fetch leads directly from database API
  useEffect(() => {
    let isMounted = true;
    const fetchLeads = async () => {
      try {
        const res = await fetch('/api/leads');
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.leads) && data.leads.length > 0 && isMounted) {
            setLeads(data.leads);
          }
        }
      } catch (err) {
        console.warn('Leads database API sync:', err);
      }
    };
    fetchLeads();
    return () => { isMounted = false; };
  }, []);

  // Filtered products list
  const filteredProducts = products.filter((p) => {
    const matchCat = categoryFilter === 'all' || p.category === categoryFilter;
    const matchStock =
      stockFilter === 'all' ||
      (stockFilter === 'inStock' && p.inStock) ||
      (stockFilter === 'outOfStock' && !p.inStock);
    const matchQuery =
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.capacity.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchStock && matchQuery;
  }).sort((a, b) => {
    if (sortBy === 'price-asc') return a.priceKsh - b.priceKsh;
    if (sortBy === 'price-desc') return b.priceKsh - a.priceKsh;
    return a.name.localeCompare(b.name);
  });

  // Filtered leads
  const filteredLeads = leads.filter((lead) => {
    const matchStatus = leadsFilter === 'all' || lead.status === leadsFilter;
    const matchQuery =
      lead.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.county.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatus && matchQuery;
  });

  // Calculate Metrics
  const totalCatalogValueKsh = products.reduce((acc, curr) => acc + curr.priceKsh, 0);
  const inStockUnits = products.filter((p) => p.inStock).length;
  const seoConfiguredCount = products.filter((p) => p.seo?.metaTitle && p.seo?.metaDescription).length;
  const seoScorePercentage = Math.round((seoConfiguredCount / Math.max(products.length, 1)) * 100);
  const pendingLeadsCount = leads.filter((l) => l.status === 'New').length;
  const totalLeadsValueKsh = leads.reduce((acc, curr) => acc + curr.totalKsh, 0);

  // Handler: Open modal for creating product
  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setModalAiPrompt('');
    setModalAiMessage(null);
    setModalTab('details');
    setFormData({
      name: '',
      category: 'water-purification',
      priceKsh: 85000,
      originalPriceKsh: undefined,
      inStock: true,
      image: DEFAULT_IMAGES[0].url,
      description: '',
      tagline: '',
      capacity: '100 Litres',
      material: 'Food-Grade Stainless Steel AISI 304',
      power: '220V 50Hz',
      features: ['Digital PLC Volume Dispenser', 'Food-Grade SS 304 Casing', '1 Year Saints Tech Warranty'],
      specs: [
        { label: 'Capacity', value: '100 Litres' },
        { label: 'Operating Voltage', value: '240V Kenyan Plug' }
      ],
      seo: {
        metaTitle: '',
        metaDescription: '',
        keywords: '',
        tags: ['Vending', 'Kenya', 'Saints Tech']
      }
    });
    setIsModalOpen(true);
  };

  // Handler: Open modal for editing product
  const handleOpenEditModal = (product: Product) => {
    setIsEditing(true);
    setEditingId(product.id);
    setModalAiPrompt(product.name);
    setModalAiMessage(null);
    setModalTab('details');
    setFormData({
      name: product.name,
      category: product.category,
      priceKsh: product.priceKsh,
      originalPriceKsh: product.originalPriceKsh,
      inStock: product.inStock,
      image: product.image,
      description: product.description,
      tagline: product.tagline,
      capacity: product.capacity,
      material: product.material,
      power: product.power,
      features: (product.features && product.features.length > 0) ? [...product.features] : [''],
      specs: (product.specs && product.specs.length > 0) ? product.specs.map((s) => ({ ...s })) : [{ label: '', value: '' }],
      seo: {
        metaTitle: product.seo?.metaTitle || `${product.name} | Saints Tech Solutions`,
        metaDescription: product.seo?.metaDescription || product.tagline,
        keywords: product.seo?.keywords || '',
        tags: product.seo?.tags ? [...product.seo.tags] : []
      }
    });
    setIsModalOpen(true);
  };

  // Handler: Save product
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;

    const cleanFeatures = formData.features.filter((f) => f.trim() !== '');
    const cleanSpecs = formData.specs.filter((s) => s.label.trim() !== '' && s.value.trim() !== '');

    const finalProductData = {
      ...formData,
      features: cleanFeatures.length > 0 ? cleanFeatures : ['Food-Grade Sanitary SS 304 Construction'],
      specs: cleanSpecs.length > 0 ? cleanSpecs : [{ label: 'Capacity', value: formData.capacity || 'Standard' }],
      seo: {
        metaTitle: formData.seo.metaTitle || `${formData.name} in Kenya | Saints Tech Solutions`,
        metaDescription: formData.seo.metaDescription || formData.tagline || formData.description.slice(0, 150),
        keywords: formData.seo.keywords || `${formData.name}, Kenya, Saints Tech Solutions`,
        tags: formData.seo.tags && formData.seo.tags.length > 0 ? formData.seo.tags : ['Kenya', 'Saints Tech']
      }
    };

    if (isEditing && editingId) {
      await updateProduct(editingId, finalProductData);
    } else {
      await addProduct(finalProductData);
    }

    setIsModalOpen(false);
  };

  // Toggle in stock
  const handleToggleStock = (product: Product) => {
    updateProduct(product.id, { inStock: !product.inStock });
  };

  // AI Generation inside Modal
  const handleGenerateModalAi = async (seoOnly = false) => {
    const inputConcept = modalAiPrompt.trim() || formData.name.trim();
    if (!inputConcept) return;

    setIsGeneratingModalAi(true);
    setModalAiMessage(null);

    try {
      const response = await fetch('/api/ai/generate-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: inputConcept,
          name: formData.name || inputConcept,
          category: formData.category,
          capacity: formData.capacity,
          targetPriceKsh: formData.priceKsh
        })
      });

      const data = await response.json();
      if (data.success && data.product) {
        const p = data.product;
        if (seoOnly) {
          setFormData((prev) => ({
            ...prev,
            seo: {
              metaTitle: p.seo?.metaTitle || prev.seo.metaTitle,
              metaDescription: p.seo?.metaDescription || prev.seo.metaDescription,
              keywords: p.seo?.keywords || prev.seo.keywords,
              tags: p.seo?.tags || prev.seo.tags
            }
          }));
          setModalAiMessage('SEO Metadata successfully refreshed with AI!');
        } else {
          setFormData((prev) => ({
            ...prev,
            name: p.name || prev.name,
            tagline: p.tagline || prev.tagline,
            description: p.description || prev.description,
            capacity: p.capacity || prev.capacity,
            material: p.material || prev.material,
            power: p.power || prev.power,
            priceKsh: p.priceKsh || prev.priceKsh,
            originalPriceKsh: p.originalPriceKsh || prev.originalPriceKsh,
            features: Array.isArray(p.features) && p.features.length > 0 ? p.features : prev.features,
            specs: Array.isArray(p.specs) && p.specs.length > 0 ? p.specs : prev.specs,
            seo: {
              metaTitle: p.seo?.metaTitle || `${p.name} in Kenya | Saints Tech Solutions`,
              metaDescription: p.seo?.metaDescription || p.tagline,
              keywords: p.seo?.keywords || '',
              tags: Array.isArray(p.seo?.tags) ? p.seo.tags : ['Kenya', 'Saints Tech']
            }
          }));
          setModalAiMessage(`Generated complete product specs & SEO via ${data.source || 'AI'}!`);
        }
      }
    } catch (err: any) {
      console.error(err);
      alert('AI generation failed: ' + err.message);
    } finally {
      setIsGeneratingModalAi(false);
    }
  };

  // Dedicated AI Studio Generation
  const handleRunAiStudio = async () => {
    if (!aiStudioPrompt.trim()) return;
    setIsGeneratingStudioAi(true);
    setStudioSuccessNotice(null);

    try {
      const response = await fetch('/api/ai/generate-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: aiStudioPrompt.trim(),
          category: aiStudioCategory,
          capacity: aiStudioCapacity,
          targetPriceKsh: aiStudioPrice
        })
      });

      const data = await response.json();
      if (data.success && data.product) {
        setGeneratedDraftProduct({
          ...data.product,
          category: aiStudioCategory,
          image: DEFAULT_IMAGES.find((i) => i.category === aiStudioCategory)?.url || DEFAULT_IMAGES[0].url,
          inStock: true
        });
      }
    } catch (err: any) {
      alert('AI Studio generation failed: ' + err.message);
    } finally {
      setIsGeneratingStudioAi(false);
    }
  };

  const handlePublishStudioDraft = () => {
    if (!generatedDraftProduct) return;
    addProduct(generatedDraftProduct);
    setStudioSuccessNotice(`"${generatedDraftProduct.name}" has been published to the live catalog!`);
    setGeneratedDraftProduct(null);
    setAiStudioPrompt('');
  };

  // Update lead status
  const handleUpdateLeadStatus = (leadId: string, newStatus: LeadInquiry['status']) => {
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l))
    );
  };

  // Export Catalog as CSV
  const handleExportCatalogCsv = () => {
    const headers = ['ID', 'Name', 'Category', 'PriceKsh', 'Capacity', 'InStock', 'Material', 'Power'];
    const rows = products.map((p) => [
      `"${p.id}"`,
      `"${p.name.replace(/"/g, '""')}"`,
      `"${p.category}"`,
      p.priceKsh,
      `"${p.capacity}"`,
      p.inStock ? 'YES' : 'NO',
      `"${p.material}"`,
      `"${p.power}"`
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Saints_Tech_Catalog_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Leads as CSV
  const handleExportLeadsCsv = () => {
    const headers = ['ID', 'Date', 'Customer', 'Business', 'Phone', 'Email', 'County', 'TotalKsh', 'Status', 'Notes'];
    const rows = leads.map((l) => [
      `"${l.id}"`,
      `"${l.date}"`,
      `"${l.customerName.replace(/"/g, '""')}"`,
      `"${l.businessName.replace(/"/g, '""')}"`,
      `"${l.phone}"`,
      `"${l.email}"`,
      `"${l.county}"`,
      l.totalKsh,
      `"${l.status}"`,
      `"${l.notes.replace(/"/g, '""')}"`
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Saints_Tech_Leads_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Selected product for SEO preview
  const currentSeoProduct = products.find((p) => p.id === selectedSeoProductId) || products[0];

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col antialiased">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-xs lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* ======================================================== */}
        {/* LEFT SIDEBAR NAVIGATION (260px Fixed / Slide-out on Mobile) */}
        {/* ======================================================== */}
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-950 border-r border-slate-800/80 flex flex-col transition-transform duration-200 ease-in-out lg:static lg:translate-x-0 ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Sidebar Top Brand Header */}
          <div className="p-5 border-b border-slate-800/80 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center text-white shadow-md shadow-sky-500/20">
                <Boxes className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="font-extrabold text-sm text-white tracking-tight leading-none truncate">
                  Saints Tech
                </div>
                <div className="text-[11px] text-sky-400 font-medium mt-1 tracking-wider uppercase">
                  Admin Console
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-1.5 text-slate-400 hover:text-white rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Return to Storefront Quick Switcher */}
          <div className="px-4 py-3 border-b border-slate-800/60">
            <button
              onClick={() => onNavigate('home')}
              className="w-full px-3 py-2 bg-slate-900 hover:bg-slate-850 hover:border-slate-700 border border-slate-800 rounded-xl text-xs font-semibold text-slate-300 hover:text-white transition-colors flex items-center justify-between group cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <ArrowLeft className="w-3.5 h-3.5 text-sky-400 group-hover:-translate-x-0.5 transition-transform" />
                <span>Return to Store</span>
              </span>
              <span className="text-[10px] text-slate-500 font-mono">Live</span>
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 p-3 space-y-6 overflow-y-auto">
            {/* Section 1: Core Operations */}
            <div>
              <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Catalog & Operations
              </div>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setActiveTab('images');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'images'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <ImageIcon className="w-4 h-4 shrink-0 text-sky-400" />
                    <span>Website Images</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setActiveTab('inventory');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'inventory'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Boxes className="w-4 h-4 shrink-0" />
                    <span>Equipment Inventory</span>
                  </div>
                  <span
                    className={`text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-md ${
                      activeTab === 'inventory' ? 'bg-sky-700 text-white' : 'bg-slate-850 text-slate-400'
                    }`}
                  >
                    {products.length}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('blogs');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'blogs'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <BookOpen className="w-4 h-4 shrink-0 text-amber-400" />
                    <span>Blog Posts CRUD</span>
                  </div>
                  <span className="text-[10px] font-bold tabular-nums text-slate-400 bg-slate-850 px-2 py-0.5 rounded-md">
                    Articles
                  </span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('inquiries');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'inquiries'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Inbox className="w-4 h-4 shrink-0" />
                    <span>Quotes & RFQ Leads</span>
                  </div>
                  {pendingLeadsCount > 0 ? (
                    <span className="text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      {pendingLeadsCount} new
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-md bg-slate-850 text-slate-400">
                      {leads.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Section 2: Automation & AI Tools */}
            <div>
              <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Automation & Intelligence
              </div>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setActiveTab('ai-studio');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'ai-studio'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Sparkles className="w-4 h-4 shrink-0 text-sky-400" />
                    <span>AI Equipment Studio</span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400">Gemini</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('schema');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'schema'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Database className="w-4 h-4 shrink-0 text-sky-400" />
                    <span>MySQL Database Schema</span>
                  </div>
                  <span className="text-[10px] font-mono text-emerald-400 bg-slate-850 px-2 py-0.5 rounded-md">
                    SQL 8.0
                  </span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('seo');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'seo'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Globe className="w-4 h-4 shrink-0" />
                    <span>SEO & SERP Visibility</span>
                  </div>
                  <span className="text-[10px] font-bold tabular-nums text-emerald-400">
                    {seoScorePercentage}%
                  </span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('analytics');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'analytics'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <BarChart3 className="w-4 h-4 shrink-0" />
                    <span>Valuation & ROI</span>
                  </div>
                </button>

                <button
                  onClick={() => onNavigate('blog')}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-slate-200 hover:bg-slate-900 transition-colors cursor-pointer"
                  title="View published technical articles & field insights"
                >
                  <div className="flex items-center gap-2.5">
                    <BookOpen className="w-4 h-4 shrink-0 text-amber-400" />
                    <span>Blog & Insights</span>
                  </div>
                  <span className="text-[10px] font-bold tabular-nums text-slate-400 bg-slate-850 px-2 py-0.5 rounded-md">
                    4 Guides
                  </span>
                </button>
              </div>
            </div>

            {/* Section 3: Configuration */}
            <div>
              <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                System & Info
              </div>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setActiveTab('settings');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    activeTab === 'settings'
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Building2 className="w-4 h-4 shrink-0" />
                    <span>Factory & Headquarters</span>
                  </div>
                </button>
              </div>
            </div>
          </nav>

          {/* Sidebar Bottom Profile & Status */}
          <div className="p-4 border-t border-slate-800/80 bg-slate-950/60 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-xs text-sky-400">
                ST
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-slate-200 truncate">Saints Operations Desk</div>
                <div className="text-[10px] text-slate-400 truncate">Juja Road Green Mall, Nairobi</div>
              </div>
            </div>
            <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-slate-850">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span>Catalog Synced</span>
              </span>
              <span className="font-mono">v1.2</span>
            </div>

            <button
              type="button"
              onClick={() => {
                performAdminLogout();
                onNavigate('login');
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-slate-900 hover:bg-rose-950/40 hover:text-rose-400 hover:border-rose-800/60 border border-slate-800 rounded-xl text-xs font-semibold text-slate-400 transition-colors cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Log Out</span>
            </button>
          </div>
        </aside>

        {/* ======================================================== */}
        {/* MAIN WORKSPACE CONTENT AREA */}
        {/* ======================================================== */}
        <div className="flex-1 flex flex-col min-w-0 bg-slate-900 overflow-y-auto">
          {/* Top Workspace Header Bar */}
          <header className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur-md border-b border-slate-800 px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden p-2 text-slate-400 hover:text-white rounded-lg border border-slate-800 hover:bg-slate-850"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Breadcrumb Trail */}
              <div className="flex items-center gap-2 text-xs text-slate-400 truncate">
                <span className="text-slate-300 font-medium">Console</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="text-sky-400 font-semibold capitalize truncate">
                  {activeTab === 'ai-studio'
                    ? 'AI Equipment Studio'
                    : activeTab === 'blogs'
                    ? 'Blog Posts & Insights CRUD'
                    : activeTab === 'schema'
                    ? 'MySQL Database Schema'
                    : activeTab === 'seo'
                    ? 'SEO & SERP Visibility'
                    : activeTab === 'inquiries'
                    ? 'Quotes & RFQ Leads'
                    : activeTab === 'analytics'
                    ? 'Valuation & ROI Simulator'
                    : activeTab === 'settings'
                    ? 'Factory Settings'
                    : activeTab === 'images'
                    ? 'Website Images'
                    : 'Equipment Inventory'}
                </span>
              </div>
            </div>

            {/* Quick Header Actions */}
            <div className="flex items-center gap-2.5 shrink-0">
              {activeTab === 'inventory' && (
                <>
                  <button
                    onClick={handleExportCatalogCsv}
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-850 hover:bg-slate-800 border border-slate-700/80 rounded-xl text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer"
                    title="Export catalog data as CSV"
                  >
                    <Download className="w-3.5 h-3.5 text-slate-400" />
                    <span>Export CSV</span>
                  </button>

                  <button
                    onClick={() => setResetCatalogConfirm(true)}
                    className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-850 hover:bg-slate-800 border border-slate-700/80 rounded-xl text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer"
                    title="Reset to initial factory equipment list"
                  >
                    <RefreshCw className="w-3.5 h-3.5 text-slate-400" />
                    <span>Reset Defaults</span>
                  </button>

                  <button
                    onClick={handleOpenCreateModal}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-xs font-bold shadow-sm shadow-sky-600/30 transition-colors cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Machine</span>
                  </button>
                </>
              )}

              {activeTab === 'inquiries' && (
                <button
                  onClick={handleExportLeadsCsv}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-850 hover:bg-slate-800 border border-slate-700 rounded-xl text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5 text-slate-400" />
                  <span>Export Leads CSV</span>
                </button>
              )}

              {/* View Live Store link */}
              <button
                onClick={() => onNavigate('home')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white text-xs font-semibold rounded-xl border border-slate-700 transition-colors cursor-pointer"
              >
                <span>Live Site</span>
                <ArrowUpRight className="w-3.5 h-3.5 text-sky-400" />
              </button>

              {/* Sign out button */}
              <button
                type="button"
                onClick={() => {
                  performAdminLogout();
                  onNavigate('login');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-rose-950/50 hover:text-rose-300 hover:border-rose-800/60 text-slate-300 text-xs font-semibold rounded-xl border border-slate-700 transition-colors cursor-pointer"
                title="Sign out of operations console"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </header>

          {/* Sub-view Rendering */}
          <main className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl w-full mx-auto">
            {activeTab === 'images' && <SiteImagesManager />}

            {/* ======================================================== */}
            {/* TAB 1: EQUIPMENT INVENTORY */}
            {/* ======================================================== */}
            {activeTab === 'inventory' && (
              <div className="space-y-6">
                {/* Stats Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Total Catalog Machines
                    </div>
                    <div className="text-2xl font-extrabold text-white tabular-nums mt-1">
                      {products.length} Units
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Across 6 major categories</div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">
                      Ready In Stock
                    </div>
                    <div className="text-2xl font-extrabold text-emerald-400 tabular-nums mt-1">
                      {inStockUnits} Units
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Available for dispatch</div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-sky-400">
                      Inventory Asset Value
                    </div>
                    <div className="text-2xl font-extrabold text-white tabular-nums mt-1">
                      KSh {formatKsh(totalCatalogValueKsh)}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Commercial valuation</div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-400">
                      SEO Optimization
                    </div>
                    <div className="text-2xl font-extrabold text-indigo-400 tabular-nums mt-1">
                      {seoScorePercentage}%
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {seoConfiguredCount} of {products.length} fully indexed
                    </div>
                  </div>
                </div>

                {/* Filter and Search Bar */}
                <div className="bg-slate-950/80 p-4 rounded-2xl border border-slate-800 space-y-3">
                  <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
                    <div className="relative flex-1 w-full">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search equipment by name, capacity, specs, or model code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full text-xs pl-10 pr-3.5 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                      <select
                        value={stockFilter}
                        onChange={(e) => setStockFilter(e.target.value as any)}
                        className="text-xs px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-200 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      >
                        <option value="all">All Stock Statuses</option>
                        <option value="inStock">In Stock Only</option>
                        <option value="outOfStock">Out of Stock Only</option>
                      </select>

                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as any)}
                        className="text-xs px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-200 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      >
                        <option value="name">Sort by Name (A-Z)</option>
                        <option value="price-desc">Price: High to Low</option>
                        <option value="price-asc">Price: Low to High</option>
                      </select>
                    </div>
                  </div>

                  {/* Category Segmented Buttons */}
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-1 pt-1 scrollbar-none">
                    {[
                      { id: 'all', label: 'All Equipment' },
                      { id: 'water-purification', label: 'Water Purification (RO)' },
                      { id: 'milk-atm', label: 'Milk ATMs' },
                      { id: 'water-vending', label: 'Water Vending Stations' },
                      { id: 'cooking-oil', label: 'Cooking Oil ATMs' },
                      { id: 'pasteurizers', label: 'Pasteurizers' },
                      { id: 'spares', label: 'Spares & Servicing' }
                    ].map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setCategoryFilter(cat.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                          categoryFilter === cat.id
                            ? 'bg-sky-600 text-white'
                            : 'bg-slate-900 hover:bg-slate-850 text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* High Density Equipment Table */}
                <div className="bg-slate-950/80 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-slate-900/90 border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                        <tr>
                          <th className="py-3.5 px-4">Equipment / Specifications</th>
                          <th className="py-3.5 px-4">Category</th>
                          <th className="py-3.5 px-4">Capacity</th>
                          <th className="py-3.5 px-4 text-right">Price (KSh)</th>
                          <th className="py-3.5 px-4 text-center">Stock State</th>
                          <th className="py-3.5 px-4">SEO State</th>
                          <th className="py-3.5 px-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-850 text-slate-300">
                        {filteredProducts.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="py-16 text-center text-slate-400">
                              <div className="max-w-xs mx-auto space-y-3">
                                <Boxes className="w-8 h-8 text-slate-400 mx-auto" />
                                <div className="text-sm font-semibold text-slate-300">No equipment matches this filter</div>
                                <p className="text-xs text-slate-400">
                                  Try searching with another keyword or reset the category filter.
                                </p>
                                <button
                                  onClick={() => {
                                    setSearchTerm('');
                                    setCategoryFilter('all');
                                    setStockFilter('all');
                                  }}
                                  className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-xs font-semibold rounded-lg text-white"
                                >
                                  Clear Filters
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredProducts.map((prod) => (
                            <tr key={prod.id} className="hover:bg-slate-900/60 transition-colors group">
                              {/* Title & Image */}
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <SafeImage
                                    src={prod.image}
                                    fallbackCategory={prod.category}
                                    alt={prod.name}
                                    className="w-12 h-12 rounded-xl object-cover bg-slate-900 shrink-0 border border-slate-800"
                                  />
                                  <div className="min-w-0 max-w-xs sm:max-w-md">
                                    <div className="font-bold text-white text-xs truncate group-hover:text-sky-400 transition-colors">
                                      {prod.name}
                                    </div>
                                    <div className="text-[11px] text-slate-400 truncate mt-0.5">
                                      {prod.tagline || prod.description.slice(0, 70)}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Category */}
                              <td className="py-3 px-4 capitalize text-slate-400 font-medium">
                                <span className="inline-block px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-[11px]">
                                  {prod.category.replace('-', ' ')}
                                </span>
                              </td>

                              {/* Capacity */}
                              <td className="py-3 px-4 font-semibold text-slate-200">
                                {prod.capacity}
                              </td>

                              {/* Price */}
                              <td className="py-3 px-4 text-right font-bold text-white tabular-nums">
                                KSh {formatKsh(prod.priceKsh)}
                              </td>

                              {/* Stock toggle */}
                              <td className="py-3 px-4 text-center">
                                <button
                                  onClick={() => handleToggleStock(prod)}
                                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold cursor-pointer transition-colors ${
                                    prod.inStock
                                      ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800/60 hover:bg-emerald-900/60'
                                      : 'bg-rose-950/80 text-rose-400 border border-rose-800/60 hover:bg-rose-900/60'
                                  }`}
                                  title="Click to toggle in-stock availability"
                                >
                                  {prod.inStock ? 'In Stock' : 'Out of Stock'}
                                </button>
                              </td>

                              {/* SEO State */}
                              <td className="py-3 px-4">
                                {prod.seo?.metaTitle ? (
                                  <div className="flex items-center gap-1.5 text-emerald-400 text-[11px] font-medium">
                                    <Globe className="w-3.5 h-3.5" />
                                    <span>Indexed</span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-[11px]">Default</span>
                                )}
                              </td>

                              {/* Action buttons */}
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => onNavigate('product-detail', prod.id)}
                                    className="p-1.5 text-slate-400 hover:text-sky-400 hover:bg-slate-850 rounded-lg transition-colors cursor-pointer"
                                    title="Open live customer view"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleOpenEditModal(prod)}
                                    className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-850 rounded-lg transition-colors cursor-pointer"
                                    title="Edit specifications & SEO"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirmId(prod.id)}
                                    className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-950/30 rounded-lg transition-colors cursor-pointer"
                                    title="Delete product"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ======================================================== */}
            {/* TAB: BLOG POSTS CRUD */}
            {/* ======================================================== */}
            {activeTab === 'blogs' && (
              <AdminBlogManager onNavigate={onNavigate} />
            )}

            {/* ======================================================== */}
            {/* TAB 2: QUOTES & RFQ LEADS */}
            {/* ======================================================== */}
            {activeTab === 'inquiries' && (
              <div className="space-y-6">
                {/* Leads Metric Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Total Inquiries Logged
                    </span>
                    <div className="text-2xl font-extrabold text-white tabular-nums mt-1">
                      {leads.length} Leads
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Commercial inquiries recorded</div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400">
                      Action Required (New)
                    </span>
                    <div className="text-2xl font-extrabold text-amber-400 tabular-nums mt-1">
                      {pendingLeadsCount} Pending
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Awaiting customer callback</div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-sky-400">
                      Total Pipeline Value
                    </span>
                    <div className="text-2xl font-extrabold text-white tabular-nums mt-1">
                      KSh {formatKsh(totalLeadsValueKsh)}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Active quotation requests</div>
                  </div>
                </div>

                {/* Filter and Status Toolbar */}
                <div className="bg-slate-950/80 p-4 rounded-2xl border border-slate-800 flex flex-col sm:flex-row gap-3 items-center justify-between">
                  <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto">
                    {(['all', 'New', 'Contacted', 'Invoiced', 'Closed'] as const).map((st) => (
                      <button
                        key={st}
                        onClick={() => setLeadsFilter(st)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                          leadsFilter === st
                            ? 'bg-sky-600 text-white'
                            : 'bg-slate-900 hover:bg-slate-850 text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        {st === 'all' ? 'All Leads' : st}
                      </button>
                    ))}
                  </div>

                  <div className="text-xs text-slate-400">
                    Direct integration with customer quote drawer on storefront
                  </div>
                </div>

                {/* Leads Table */}
                <div className="bg-slate-950/80 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-slate-900/90 border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                        <tr>
                          <th className="py-3.5 px-4">RFQ Ref / Date</th>
                          <th className="py-3.5 px-4">Client & Business</th>
                          <th className="py-3.5 px-4">Location / Phone</th>
                          <th className="py-3.5 px-4">Equipment Inquired</th>
                          <th className="py-3.5 px-4 text-right">Estimate (KSh)</th>
                          <th className="py-3.5 px-4 text-center">Status</th>
                          <th className="py-3.5 px-4 text-right">Quick Contact</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-850 text-slate-300">
                        {filteredLeads.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="py-12 text-center text-slate-400">
                              No inquiries found under this status filter.
                            </td>
                          </tr>
                        ) : (
                          filteredLeads.map((lead) => (
                            <tr key={lead.id} className="hover:bg-slate-900/60 transition-colors">
                              {/* Ref & Date */}
                              <td className="py-3.5 px-4">
                                <div className="font-mono font-bold text-sky-400">{lead.id}</div>
                                <div className="text-[11px] text-slate-400">{lead.date}</div>
                              </td>

                              {/* Customer & Business */}
                              <td className="py-3.5 px-4">
                                <div className="font-bold text-white">{lead.customerName}</div>
                                <div className="text-[11px] text-slate-400 truncate max-w-[160px]">
                                  {lead.businessName}
                                </div>
                              </td>

                              {/* Location & Phone */}
                              <td className="py-3.5 px-4">
                                <div className="font-medium text-slate-200">{lead.county}, Kenya</div>
                                <div className="text-[11px] text-slate-400 tabular-nums">{lead.phone}</div>
                              </td>

                              {/* Items */}
                              <td className="py-3.5 px-4">
                                <div className="max-w-xs space-y-1">
                                  {(lead.items || []).map((it, idx) => (
                                    <div key={idx} className="text-[11px] text-slate-300 truncate">
                                      <span className="font-bold text-sky-400">{it.quantity}x</span> {it.name}
                                    </div>
                                  ))}
                                </div>
                              </td>

                              {/* Total Price */}
                              <td className="py-3.5 px-4 text-right font-bold text-white tabular-nums">
                                KSh {formatKsh(lead.totalKsh)}
                              </td>

                              {/* Status Dropdown */}
                              <td className="py-3.5 px-4 text-center">
                                <select
                                  value={lead.status}
                                  onChange={(e) => handleUpdateLeadStatus(lead.id, e.target.value as any)}
                                  className={`px-2.5 py-1 rounded-md text-[11px] font-bold border focus:outline-hidden cursor-pointer ${
                                    lead.status === 'New'
                                      ? 'bg-amber-950/80 text-amber-400 border-amber-800/80'
                                      : lead.status === 'Contacted'
                                      ? 'bg-sky-950/80 text-sky-400 border-sky-800/80'
                                      : lead.status === 'Invoiced'
                                      ? 'bg-indigo-950/80 text-indigo-400 border-indigo-800/80'
                                      : 'bg-emerald-950/80 text-emerald-400 border-emerald-800/80'
                                  }`}
                                >
                                  <option value="New">New</option>
                                  <option value="Contacted">Contacted</option>
                                  <option value="Invoiced">Invoiced</option>
                                  <option value="Closed">Closed</option>
                                </select>
                              </td>

                              {/* Actions */}
                              <td className="py-3.5 px-4 text-right">
                                <div className="flex items-center justify-end gap-1.5">
                                  <a
                                    href={`tel:${lead.phone.replace(/[^0-9+]/g, '')}`}
                                    className="p-1.5 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-emerald-400 rounded-lg border border-slate-800 transition-colors"
                                    title="Call Customer"
                                  >
                                    <Phone className="w-3.5 h-3.5" />
                                  </a>
                                  <a
                                    href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}?text=Hello%20${encodeURIComponent(
                                      lead.customerName
                                    )},%20this%20is%20Saints%20Tech%20Solutions%20Nairobi%20regarding%20your%20inquiry%20${lead.id}.`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="p-1.5 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-emerald-400 rounded-lg border border-slate-800 transition-colors"
                                    title="Chat on WhatsApp"
                                  >
                                    <MessageSquare className="w-3.5 h-3.5" />
                                  </a>
                                  <button
                                    onClick={() => setSelectedLead(lead)}
                                    className="p-1.5 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-sky-400 rounded-lg border border-slate-800 transition-colors"
                                    title="View Full Inquiry Details"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ======================================================== */}
            {/* TAB 3: AI EQUIPMENT STUDIO (GEMINI 3.8 FLASH) */}
            {/* ======================================================== */}
            {activeTab === 'ai-studio' && (
              <div className="space-y-6">
                <div className="p-6 bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950 rounded-2xl border border-slate-800 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-400">
                        <Sparkles className="w-4 h-4 text-sky-400" />
                        <span>Gemini 3.8 Flash Industrial Generator</span>
                      </div>
                      <h2 className="text-xl font-bold text-white mt-1">
                        Automated Equipment Specification & SEO Studio
                      </h2>
                      <p className="text-xs text-slate-300 mt-0.5">
                        Rapidly draft food-grade specifications, technical attributes, and East African SEO meta tags.
                      </p>
                    </div>

                    <div className="px-3 py-1.5 rounded-xl bg-slate-900/80 border border-slate-700/80 text-[11px] text-slate-300 font-mono">
                      model: gemini-3.8-flash
                    </div>
                  </div>

                  {/* Preset Quick Chips */}
                  <div>
                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                      Prompt Presets for Kenyan Commercial Market:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        '2000 Litres/hr Commercial Reverse Osmosis Water Treatment Plant with UV Sterilizer',
                        '500 Liters Steam-Jacketed Stainless Steel 304 Batch Milk Pasteurizer with Agitator',
                        '3-Nozzle Automated Salad Cooking Oil ATM Dispenser with GSM Sales Counter',
                        'Solar-Powered Twin-Dispense Digital Milk ATM 300 Litres for Rural Dairies',
                        'Coin & M-Pesa Automated Commercial Water Vending Kiosk with Reverse Osmosis'
                      ].map((preset, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            setAiStudioPrompt(preset);
                            if (preset.includes('Water')) setAiStudioCategory('water-purification');
                            else if (preset.includes('Milk ATM')) setAiStudioCategory('milk-atm');
                            else if (preset.includes('Pasteurizer')) setAiStudioCategory('pasteurizers');
                            else if (preset.includes('Oil')) setAiStudioCategory('cooking-oil');
                          }}
                          className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-[11px] text-slate-300 hover:text-white transition-colors cursor-pointer text-left"
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Input Form */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                    <div className="sm:col-span-3">
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Equipment Concept or Commercial Brief *
                      </label>
                      <input
                        type="text"
                        value={aiStudioPrompt}
                        onChange={(e) => setAiStudioPrompt(e.target.value)}
                        placeholder="e.g. 5000 Litres/hr Industrial RO Plant for Borehole Water Bottling in Machakos"
                        className="w-full text-xs px-4 py-3 bg-slate-900 border border-slate-750 rounded-xl text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">Target Category</label>
                      <select
                        value={aiStudioCategory}
                        onChange={(e) => setAiStudioCategory(e.target.value as any)}
                        className="w-full text-xs px-3 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      >
                        <option value="water-purification">Water Purification (RO)</option>
                        <option value="milk-atm">Milk ATMs</option>
                        <option value="water-vending">Water Vending Stations</option>
                        <option value="cooking-oil">Cooking Oil ATMs</option>
                        <option value="pasteurizers">Milk Pasteurizers</option>
                        <option value="spares">Spares & Servicing</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">Capacity Hint</label>
                      <input
                        type="text"
                        value={aiStudioCapacity}
                        onChange={(e) => setAiStudioCapacity(e.target.value)}
                        placeholder="e.g. 1000 Litres / hr"
                        className="w-full text-xs px-3 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      >
                      </input>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">Target Price (KSh)</label>
                      <input
                        type="number"
                        value={aiStudioPrice}
                        onChange={(e) => setAiStudioPrice(Number(e.target.value))}
                        className="w-full text-xs font-bold px-3 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <button
                      type="button"
                      disabled={isGeneratingStudioAi || !aiStudioPrompt.trim()}
                      onClick={handleRunAiStudio}
                      className="px-5 py-3 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow-lg shadow-sky-600/30"
                    >
                      {isGeneratingStudioAi ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      <span>
                        {isGeneratingStudioAi ? 'Consulting Gemini 3.8...' : 'Generate Equipment Specs & SEO'}
                      </span>
                    </button>

                    {studioSuccessNotice && (
                      <div className="text-xs text-emerald-400 flex items-center gap-1.5 font-medium">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{studioSuccessNotice}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Draft Preview Result */}
                {generatedDraftProduct && (
                  <div className="bg-slate-950/80 rounded-2xl border border-slate-800 p-6 space-y-6 animate-fadeIn">
                    <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400">
                          Generated Product Draft
                        </span>
                        <h3 className="text-lg font-bold text-white mt-0.5">{generatedDraftProduct.name}</h3>
                        <p className="text-xs text-slate-400">{generatedDraftProduct.tagline}</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setGeneratedDraftProduct(null)}
                          className="px-3 py-2 text-xs font-semibold text-slate-400 hover:text-white"
                        >
                          Discard
                        </button>
                        <button
                          onClick={handlePublishStudioDraft}
                          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-lg shadow-emerald-600/30 cursor-pointer"
                        >
                          <Check className="w-4 h-4" />
                          <span>Publish to Catalog</span>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-2 space-y-4">
                        <div>
                          <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                            Commercial Description
                          </h4>
                          <p className="text-xs text-slate-400 leading-relaxed">
                            {generatedDraftProduct.description}
                          </p>
                        </div>

                        <div>
                          <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                            Engineered Features Checklist
                          </h4>
                          <ul className="space-y-1.5">
                            {generatedDraftProduct.features?.map((f: string, i: number) => (
                              <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                                <Check className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                            Technical Attributes
                          </h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {generatedDraftProduct.specs?.map((s: any, i: number) => (
                              <div key={i} className="p-2 bg-slate-900 rounded-lg border border-slate-800">
                                <div className="text-[10px] text-slate-400 uppercase">{s.label}</div>
                                <div className="font-semibold text-slate-200">{s.value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right column: SEO & Pricing */}
                      <div className="space-y-4">
                        <div className="p-4 bg-slate-900 rounded-xl border border-slate-800 space-y-2">
                          <span className="text-[10px] uppercase font-bold text-slate-400">Recommended Price</span>
                          <div className="text-2xl font-extrabold text-white tabular-nums">
                            KSh {formatKsh(generatedDraftProduct.priceKsh)}
                          </div>
                          <div className="text-xs text-slate-400">
                            Capacity: <span className="font-semibold text-slate-200">{generatedDraftProduct.capacity}</span>
                          </div>
                          <div className="text-xs text-slate-400">
                            Material: <span className="font-semibold text-slate-200">{generatedDraftProduct.material}</span>
                          </div>
                        </div>

                        <div className="p-4 bg-sky-950/40 rounded-xl border border-sky-900/60 space-y-2">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-sky-300">
                            <Globe className="w-3.5 h-3.5" />
                            <span>Generated SEO Configuration</span>
                          </div>
                          <div className="text-xs font-semibold text-white">
                            {generatedDraftProduct.seo?.metaTitle}
                          </div>
                          <div className="text-[11px] text-slate-400 line-clamp-3">
                            {generatedDraftProduct.seo?.metaDescription}
                          </div>
                          <div className="flex flex-wrap gap-1 pt-1">
                            {generatedDraftProduct.seo?.tags?.map((t: string, i: number) => (
                              <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-900 text-sky-300 rounded">
                                #{t}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ======================================================== */}
            {/* TAB: MYSQL PRODUCTION DATABASE SCHEMA */}
            {/* ======================================================== */}
            {activeTab === 'schema' && (
              <AdminDatabaseSchema />
            )}

            {/* ======================================================== */}
            {/* TAB 4: SEO & SERP VISIBILITY AUDITOR */}
            {/* ======================================================== */}
            {activeTab === 'seo' && (
              <div className="space-y-6">
                {/* Header Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Catalog SEO Health
                    </span>
                    <div className="text-2xl font-extrabold text-emerald-400 tabular-nums mt-1">
                      {seoScorePercentage}% Complete
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {seoConfiguredCount} of {products.length} machines indexed
                    </div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-sky-400">
                      Average Title Length
                    </span>
                    <div className="text-2xl font-extrabold text-white tabular-nums mt-1">
                      52 Chars
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Recommended: 30-60 characters</div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-400">
                      Target Search Region
                    </span>
                    <div className="text-2xl font-extrabold text-white mt-1">
                      Nairobi, Kenya
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">East African commercial intent</div>
                  </div>
                </div>

                {/* Live Google SERP Simulator */}
                <div className="bg-slate-950/80 rounded-2xl border border-slate-800 p-6 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-white flex items-center gap-2">
                        <Globe className="w-4 h-4 text-sky-400" />
                        <span>Google Search SERP Result Simulator</span>
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Test how each equipment listing appears in Google Kenya search results.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center bg-slate-900 p-0.5 rounded-lg border border-slate-800">
                        <button
                          type="button"
                          onClick={() => setSeoPreviewDevice('desktop')}
                          className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors cursor-pointer ${
                            seoPreviewDevice === 'desktop' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          <Monitor className="w-3.5 h-3.5 inline mr-1" />
                          <span>Desktop</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSeoPreviewDevice('mobile')}
                          className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors cursor-pointer ${
                            seoPreviewDevice === 'mobile' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          <Smartphone className="w-3.5 h-3.5 inline mr-1" />
                          <span>Mobile</span>
                        </button>
                      </div>

                      <select
                        value={selectedSeoProductId}
                        onChange={(e) => setSelectedSeoProductId(e.target.value)}
                        className="text-xs px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-white"
                      >
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Google Preview Canvas */}
                  <div className="p-6 bg-white rounded-xl border border-slate-200 text-slate-800 shadow-sm max-w-2xl">
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mb-1">
                      <span className="font-semibold text-slate-700">Saints Tech Solutions</span>
                      <span>·</span>
                      <span className="truncate">
                        https://saintstech.co.ke/products/{currentSeoProduct?.id}
                      </span>
                    </div>

                    <div className="text-base sm:text-lg font-semibold text-blue-800 hover:underline cursor-pointer leading-snug">
                      {currentSeoProduct?.seo?.metaTitle || `${currentSeoProduct?.name} in Kenya | Saints Tech`}
                    </div>

                    <p className="text-xs sm:text-sm text-slate-600 mt-1 line-clamp-2 leading-relaxed">
                      {currentSeoProduct?.seo?.metaDescription ||
                        currentSeoProduct?.tagline ||
                        'Commercial food-grade equipment manufactured in Nairobi, Kenya. Juja Road Green Mall.'}
                    </p>

                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-2.5 pt-2 border-t border-slate-100">
                      <span>Price: <strong className="text-slate-800">KSh {formatKsh(currentSeoProduct?.priceKsh || 0)}</strong></span>
                      <span>·</span>
                      <span>Availability: <strong className="text-emerald-700">In Stock (Nairobi)</strong></span>
                      <span>·</span>
                      <span>Warranty: <strong>1 Year Factory</strong></span>
                    </div>
                  </div>
                </div>

                {/* SEO Audit Table */}
                <div className="bg-slate-950/80 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
                  <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                    <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                      Catalog SEO Metadata Audit
                    </h3>
                    <span className="text-xs text-slate-400">
                      Click any row to configure SEO tags
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-slate-900/90 border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                        <tr>
                          <th className="py-3 px-4">Equipment</th>
                          <th className="py-3 px-4">Meta Title (30-60 chars)</th>
                          <th className="py-3 px-4">Meta Description (120-160 chars)</th>
                          <th className="py-3 px-4 text-center">Score</th>
                          <th className="py-3 px-4 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-850 text-slate-300">
                        {products.map((p) => {
                          const titleLen = p.seo?.metaTitle?.length || 0;
                          const descLen = p.seo?.metaDescription?.length || 0;
                          const isComplete = titleLen >= 25 && descLen >= 60;

                          return (
                            <tr key={p.id} className="hover:bg-slate-900/60 transition-colors">
                              <td className="py-3 px-4 font-bold text-white truncate max-w-[200px]">
                                {p.name}
                              </td>

                              <td className="py-3 px-4">
                                <div className="text-slate-200 truncate max-w-[240px]">
                                  {p.seo?.metaTitle || '—'}
                                </div>
                                <div className="text-[10px] text-slate-400 tabular-nums">
                                  {titleLen} characters
                                </div>
                              </td>

                              <td className="py-3 px-4">
                                <div className="text-slate-400 truncate max-w-[280px]">
                                  {p.seo?.metaDescription || '—'}
                                </div>
                                <div className="text-[10px] text-slate-400 tabular-nums">
                                  {descLen} characters
                                </div>
                              </td>

                              <td className="py-3 px-4 text-center">
                                <span
                                  className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                                    isComplete
                                      ? 'bg-emerald-950 text-emerald-400 border border-emerald-800/80'
                                      : 'bg-amber-950 text-amber-400 border border-amber-800/80'
                                  }`}
                                >
                                  {isComplete ? 'Optimal' : 'Needs Polish'}
                                </span>
                              </td>

                              <td className="py-3 px-4 text-right">
                                <button
                                  onClick={() => handleOpenEditModal(p)}
                                  className="px-2.5 py-1 bg-slate-900 hover:bg-slate-800 text-sky-400 text-xs font-semibold rounded-lg border border-slate-800"
                                >
                                  Edit SEO
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ======================================================== */}
            {/* TAB 5: VALUATION & ROI SIMULATOR */}
            {/* ======================================================== */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                {/* Financial Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Total Catalog Asset Worth
                    </span>
                    <div className="text-2xl font-extrabold text-white tabular-nums mt-1">
                      KSh {formatKsh(totalCatalogValueKsh)}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Based on factory retail pricing</div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">
                      In-Stock Immediate Value
                    </span>
                    <div className="text-2xl font-extrabold text-emerald-400 tabular-nums mt-1">
                      KSh{' '}
                      {formatKsh(
                        products.filter((p) => p.inStock).reduce((acc, curr) => acc + curr.priceKsh, 0)
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Ready for dispatch inventory</div>
                  </div>

                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-sky-400">
                      Average Machine Unit Price
                    </span>
                    <div className="text-2xl font-extrabold text-white tabular-nums mt-1">
                      KSh {formatKsh(Math.round(totalCatalogValueKsh / Math.max(products.length, 1)))}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Across all product lines</div>
                  </div>
                </div>

                {/* Category Inventory Breakdown */}
                <div className="bg-slate-950/80 rounded-2xl border border-slate-800 p-6 space-y-4">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    Equipment Category Distribution
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { cat: 'water-purification', label: 'Water Purification (RO)' },
                      { cat: 'milk-atm', label: 'Milk ATMs' },
                      { cat: 'water-vending', label: 'Water Vending Stations' },
                      { cat: 'cooking-oil', label: 'Cooking Oil ATMs' },
                      { cat: 'pasteurizers', label: 'Milk Pasteurizers' },
                      { cat: 'spares', label: 'Spares & Servicing' }
                    ].map(({ cat, label }) => {
                      const count = products.filter((p) => p.category === cat).length;
                      const val = products
                        .filter((p) => p.category === cat)
                        .reduce((acc, curr) => acc + curr.priceKsh, 0);

                      return (
                        <div key={cat} className="p-4 bg-slate-900 rounded-xl border border-slate-800">
                          <div className="text-xs font-bold text-slate-200">{label}</div>
                          <div className="flex items-baseline justify-between mt-2">
                            <span className="text-lg font-extrabold text-white tabular-nums">{count} Units</span>
                            <span className="text-xs text-sky-400 font-bold tabular-nums">
                              KSh {formatKsh(val)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Interactive Commercial ROI & Profit Simulator */}
                <div className="bg-slate-950/80 rounded-2xl border border-slate-800 p-6 space-y-6">
                  <div>
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                      Commercial ROI & Margin Estimator (Kenyan Market)
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Calculate prospective daily, monthly, and annual returns for equipment buyers based on real Kenyan trading figures.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">Equipment Type</label>
                      <select
                        value={roiMachineType}
                        onChange={(e) => {
                          const val = e.target.value as any;
                          setRoiMachineType(val);
                          if (val === 'water') {
                            setRoiDailyVolume(800);
                            setRoiCostPerLitre(1.5);
                            setRoiSalePerLitre(5);
                          } else if (val === 'milk') {
                            setRoiDailyVolume(300);
                            setRoiCostPerLitre(55);
                            setRoiSalePerLitre(75);
                          } else {
                            setRoiDailyVolume(150);
                            setRoiCostPerLitre(160);
                            setRoiSalePerLitre(210);
                          }
                        }}
                        className="w-full text-xs px-3 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      >
                        <option value="milk">Milk ATM Dispenser</option>
                        <option value="water">Water Refill / Vending Station</option>
                        <option value="oil">Cooking Oil ATM</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Daily Volume Dispensed (Litres)
                      </label>
                      <input
                        type="number"
                        value={roiDailyVolume}
                        onChange={(e) => setRoiDailyVolume(Number(e.target.value))}
                        className="w-full text-xs px-3 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white font-bold tabular-nums"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Purchase Cost per Litre (KSh)
                      </label>
                      <input
                        type="number"
                        value={roiCostPerLitre}
                        onChange={(e) => setRoiCostPerLitre(Number(e.target.value))}
                        className="w-full text-xs px-3 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white font-bold tabular-nums"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Retail Sale Price per Litre (KSh)
                      </label>
                      <input
                        type="number"
                        value={roiSalePerLitre}
                        onChange={(e) => setRoiSalePerLitre(Number(e.target.value))}
                        className="w-full text-xs px-3 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white font-bold tabular-nums"
                      />
                    </div>
                  </div>

                  {/* Calculations */}
                  {(() => {
                    const marginPerLitre = Math.max(0, roiSalePerLitre - roiCostPerLitre);
                    const dailyGrossProfit = marginPerLitre * roiDailyVolume;
                    const monthlyGrossProfit = dailyGrossProfit * 30;
                    const sampleMachineCost =
                      roiMachineType === 'milk' ? 175000 : roiMachineType === 'water' ? 145000 : 165000;
                    const daysToBreakeven = dailyGrossProfit > 0 ? Math.ceil(sampleMachineCost / dailyGrossProfit) : 0;

                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 p-5 bg-sky-950/40 rounded-xl border border-sky-900/60">
                        <div>
                          <div className="text-[10px] uppercase font-bold text-sky-400">Margin Per Litre</div>
                          <div className="text-xl font-extrabold text-white tabular-nums mt-0.5">
                            KSh {marginPerLitre}
                          </div>
                        </div>

                        <div>
                          <div className="text-[10px] uppercase font-bold text-sky-400">Daily Gross Margin</div>
                          <div className="text-xl font-extrabold text-white tabular-nums mt-0.5">
                            KSh {formatKsh(dailyGrossProfit)}
                          </div>
                        </div>

                        <div>
                          <div className="text-[10px] uppercase font-bold text-emerald-400">
                            Monthly Gross Profit (30 Days)
                          </div>
                          <div className="text-xl font-extrabold text-emerald-400 tabular-nums mt-0.5">
                            KSh {formatKsh(monthlyGrossProfit)}
                          </div>
                        </div>

                        <div>
                          <div className="text-[10px] uppercase font-bold text-indigo-400">
                            Estimated Payback Period
                          </div>
                          <div className="text-xl font-extrabold text-white tabular-nums mt-0.5">
                            ~{daysToBreakeven} Days
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* ======================================================== */}
            {/* TAB 6: FACTORY SETTINGS & EXPORT */}
            {/* ======================================================== */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-slate-950/80 rounded-2xl border border-slate-800 p-6 space-y-6">
                  <div>
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                      Saints Tech Solutions Operations Headquarters
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Verified manufacturing facility specifications & administrative configurations.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div className="p-4 bg-slate-900 rounded-xl border border-slate-800 space-y-1">
                      <div className="font-bold text-white">Physical Workshop & Showroom</div>
                      <div className="text-slate-400">Juja Road 478/B Green Mall, Nairobi, Kenya</div>
                      <div className="text-sky-400 pt-1">Coordinates: 1.2721° S, 36.8524° E</div>
                    </div>

                    <div className="p-4 bg-slate-900 rounded-xl border border-slate-800 space-y-1">
                      <div className="font-bold text-white">Direct Engineering Hotlines</div>
                      <div className="text-slate-300 font-mono">+254 727 072 393</div>
                      <div className="text-slate-400">Email: info@saintstech.co.ke</div>
                    </div>

                    <div className="p-4 bg-slate-900 rounded-xl border border-slate-800 space-y-1">
                      <div className="font-bold text-white">Standards & KeBS Compliance</div>
                      <div className="text-slate-400">
                        Stainless Steel AISI 304 / 316 food-grade contact surfaces with ISO calibration alignment.
                      </div>
                    </div>

                    <div className="p-4 bg-slate-900 rounded-xl border border-slate-800 space-y-1">
                      <div className="font-bold text-white">Factory Warranty Terms</div>
                      <div className="text-slate-400">
                        12-Month Comprehensive Warranty on PLC electronic control boards, pumps, and fabrication.
                      </div>
                    </div>
                  </div>

                  {/* Catalog Backups & Maintenance */}
                  <div className="pt-4 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-bold text-xs text-white">Catalog Data Recovery & Maintenance</div>
                      <div className="text-[11px] text-slate-400">
                        Export raw JSON database backup or restore initial factory preset machines.
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleExportCatalogCsv}
                        className="px-3.5 py-2 bg-slate-850 hover:bg-slate-800 border border-slate-700 rounded-xl text-xs font-semibold text-slate-200 transition-colors cursor-pointer flex items-center gap-1.5"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Download Catalog Backup (CSV)</span>
                      </button>

                      <button
                        onClick={() => setResetCatalogConfirm(true)}
                        className="px-3.5 py-2 bg-rose-950/40 hover:bg-rose-900/60 border border-rose-800/60 text-rose-300 rounded-xl text-xs font-semibold transition-colors cursor-pointer flex items-center gap-1.5"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Factory Reset Catalog</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* ======================================================== */}
      {/* PRODUCT CREATE / EDIT MODAL */}
      {/* ======================================================== */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-xs transition-opacity"
            onClick={() => setIsModalOpen(false)}
          />

          <div className="min-h-full flex items-center justify-center p-4 sm:p-6">
            <div className="relative bg-slate-950 rounded-3xl max-w-4xl w-full shadow-2xl overflow-hidden border border-slate-800 my-8">
              {/* Modal Top Header */}
              <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/80">
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {isEditing ? 'Edit Machine Specifications & SEO' : 'Create New Machine & Configure SEO'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Saints Tech Solutions • Nairobi Manufacturing Console
                  </p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Tab Switcher */}
              <div className="px-6 pt-3 border-b border-slate-800 flex items-center gap-2 bg-slate-900/40">
                {[
                  { id: 'details', label: '1. Machine Details' },
                  { id: 'specs', label: '2. Specifications & Features' },
                  { id: 'seo', label: '3. SEO & Google Metadata' }
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setModalTab(t.id as any)}
                    className={`px-3 py-2 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                      modalTab === t.id
                        ? 'border-sky-500 text-sky-400'
                        : 'border-transparent text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Form Content */}
              <form onSubmit={handleSaveProduct} className="p-6 sm:p-8 space-y-6 max-h-[75vh] overflow-y-auto">
                {/* AI Assistant Banner */}
                <div className="bg-gradient-to-r from-sky-950/80 via-slate-900 to-indigo-950/80 rounded-2xl p-4 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-400">
                      <Sparkles className="w-4 h-4 text-sky-400" />
                      <span>Smart Specification Auto-Fill (Gemini 3.8)</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      placeholder="e.g. 500L/hr Reverse Osmosis Water Purifier with UV and Ozone"
                      value={modalAiPrompt}
                      onChange={(e) => setModalAiPrompt(e.target.value)}
                      className="flex-1 text-xs px-3.5 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isGeneratingModalAi}
                        onClick={() => handleGenerateModalAi(false)}
                        className="px-3.5 py-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer"
                      >
                        {isGeneratingModalAi ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        <span>{isGeneratingModalAi ? 'Generating...' : 'Auto-Fill with AI'}</span>
                      </button>

                      <button
                        type="button"
                        disabled={isGeneratingModalAi}
                        onClick={() => handleGenerateModalAi(true)}
                        className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-sky-300 text-xs font-semibold rounded-xl transition-colors"
                      >
                        SEO Only
                      </button>
                    </div>
                  </div>

                  {modalAiMessage && (
                    <div className="p-2 bg-emerald-950/60 border border-emerald-800/80 rounded-lg text-xs text-emerald-300 flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 shrink-0" />
                      <span>{modalAiMessage}</span>
                    </div>
                  )}
                </div>

                {/* TAB 1: Basic Machine Info */}
                {modalTab === 'details' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-300 mb-1">
                          Equipment Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="e.g. 500 Litres/hr Reverse Osmosis Commercial Purifier"
                          className="w-full text-xs px-3.5 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                          className="w-full text-xs px-3.5 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white"
                        >
                          <option value="water-purification">Water Purification (RO)</option>
                          <option value="milk-atm">Milk ATMs</option>
                          <option value="water-vending">Water Vending Stations</option>
                          <option value="cooking-oil">Cooking Oil ATMs</option>
                          <option value="pasteurizers">Milk Pasteurizers</option>
                          <option value="spares">Spares & Servicing</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">Throughput / Capacity</label>
                        <input
                          type="text"
                          value={formData.capacity}
                          onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                          placeholder="e.g. 500 Litres / hr"
                          className="w-full text-xs px-3.5 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">Price (KSh) *</label>
                        <input
                          type="number"
                          required
                          value={formData.priceKsh}
                          onChange={(e) => setFormData({ ...formData, priceKsh: Number(e.target.value) })}
                          className="w-full text-xs font-bold px-3.5 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white tabular-nums"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">
                          Original Price (Optional Strikethrough)
                        </label>
                        <input
                          type="number"
                          value={formData.originalPriceKsh || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              originalPriceKsh: e.target.value ? Number(e.target.value) : undefined
                            })
                          }
                          placeholder="e.g. 95000"
                          className="w-full text-xs px-3.5 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white tabular-nums"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">Short Tagline</label>
                      <input
                        type="text"
                        value={formData.tagline}
                        onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                        placeholder="e.g. High-efficiency commercial purification plant for bottled-grade water."
                        className="w-full text-xs px-3.5 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Detailed Commercial Description
                      </label>
                      <textarea
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Comprehensive technical and business description..."
                        className="w-full text-xs px-3.5 py-2.5 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      />
                    </div>

                    {/* Equipment Photo Asset Uploader & Selector */}
                    <ImageSelectorField
                      value={formData.image}
                      onChange={(newUrl) => setFormData({ ...formData, image: newUrl })}
                      label="Equipment Photo Asset"
                      description="Upload custom workshop machinery photos or select from uploaded assets & presets."
                      categoryHint="equipment"
                    />

                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="checkbox"
                        id="inStockCheckModal"
                        checked={formData.inStock}
                        onChange={(e) => setFormData({ ...formData, inStock: e.target.checked })}
                        className="rounded accent-sky-500 cursor-pointer"
                      />
                      <label htmlFor="inStockCheckModal" className="text-xs font-semibold text-slate-200 cursor-pointer">
                        Mark as Ready In Stock (Immediate Dispatch in Nairobi)
                      </label>
                    </div>
                  </div>
                )}

                {/* TAB 2: Technical Specifications & Features */}
                {modalTab === 'specs' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">Material Grade</label>
                        <input
                          type="text"
                          value={formData.material}
                          onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                          placeholder="Food-Grade Stainless Steel AISI 304"
                          className="w-full text-xs px-3 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">
                          Electrical / Power Spec
                        </label>
                        <input
                          type="text"
                          value={formData.power}
                          onChange={(e) => setFormData({ ...formData, power: e.target.value })}
                          placeholder="Single Phase 220V 50Hz / 1.5kW"
                          className="w-full text-xs px-3 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white"
                        />
                      </div>
                    </div>

                    {/* Features checklist */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-slate-300">Key Features Checklist</label>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, features: [...formData.features, ''] })}
                          className="text-[11px] text-sky-400 font-bold hover:underline"
                        >
                          + Add Feature
                        </button>
                      </div>
                      <div className="space-y-2">
                        {formData.features.map((feat, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={feat}
                              onChange={(e) => {
                                const updated = [...formData.features];
                                updated[idx] = e.target.value;
                                setFormData({ ...formData, features: updated });
                              }}
                              placeholder="e.g. GSM automated SMS audit module"
                              className="flex-1 text-xs px-3 py-1.5 bg-slate-900 border border-slate-750 rounded-lg text-white"
                            />
                            {formData.features.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    features: formData.features.filter((_, i) => i !== idx)
                                  });
                                }}
                                className="text-rose-400 hover:text-rose-300 p-1"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Specs Table */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-slate-300">
                          Technical Specs Table (Label & Value)
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              specs: [...formData.specs, { label: '', value: '' }]
                            })
                          }
                          className="text-[11px] text-sky-400 font-bold hover:underline"
                        >
                          + Add Spec Row
                        </button>
                      </div>
                      <div className="space-y-2">
                        {formData.specs.map((spec, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={spec.label}
                              onChange={(e) => {
                                const updated = [...formData.specs];
                                updated[idx].label = e.target.value;
                                setFormData({ ...formData, specs: updated });
                              }}
                              placeholder="Label (e.g. Flow Rate)"
                              className="w-1/3 text-xs px-3 py-1.5 bg-slate-900 border border-slate-750 rounded-lg text-white"
                            />
                            <input
                              type="text"
                              value={spec.value}
                              onChange={(e) => {
                                const updated = [...formData.specs];
                                updated[idx].value = e.target.value;
                                setFormData({ ...formData, specs: updated });
                              }}
                              placeholder="Value (e.g. 500 L/h)"
                              className="flex-1 text-xs px-3 py-1.5 bg-slate-900 border border-slate-750 rounded-lg text-white"
                            />
                            {formData.specs.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    specs: formData.specs.filter((_, i) => i !== idx)
                                  });
                                }}
                                className="text-rose-400 hover:text-rose-300 p-1"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 3: SEO Configuration */}
                {modalTab === 'seo' && (
                  <div className="space-y-4">
                    {/* Snippet Preview */}
                    <div className="p-4 bg-white text-slate-800 rounded-xl border border-slate-200 space-y-1">
                      <div className="text-[11px] text-slate-500 truncate">
                        https://saintstech.co.ke/products/{formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                      </div>
                      <div className="text-sm font-semibold text-blue-700 truncate">
                        {formData.seo.metaTitle || `${formData.name || 'Equipment'} in Kenya | Saints Tech`}
                      </div>
                      <p className="text-xs text-slate-600 line-clamp-2">
                        {formData.seo.metaDescription ||
                          formData.tagline ||
                          'Commercial equipment fabricated in Nairobi, Kenya. Juja Road Green Mall.'}
                      </p>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold text-slate-300 mb-1">
                        <span>Meta Title (Recommended 30-60 chars)</span>
                        <span className="text-slate-400 font-normal">
                          {formData.seo.metaTitle?.length || 0} characters
                        </span>
                      </div>
                      <input
                        type="text"
                        value={formData.seo.metaTitle || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            seo: { ...formData.seo, metaTitle: e.target.value }
                          })
                        }
                        placeholder="e.g. 500L/hr Reverse Osmosis Water Purifier in Kenya | Saints Tech"
                        className="w-full text-xs px-3.5 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold text-slate-300 mb-1">
                        <span>Meta Description (Recommended 120-160 chars)</span>
                        <span className="text-slate-400 font-normal">
                          {formData.seo.metaDescription?.length || 0} characters
                        </span>
                      </div>
                      <textarea
                        rows={2}
                        value={formData.seo.metaDescription || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            seo: { ...formData.seo, metaDescription: e.target.value }
                          })
                        }
                        placeholder="e.g. Buy high quality commercial 500L/hr Reverse Osmosis water purification plants in Nairobi. Food-grade SS 304, KeBS certified, 1 year warranty."
                        className="w-full text-xs px-3.5 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Meta Keywords (Comma-Separated)
                      </label>
                      <input
                        type="text"
                        value={formData.seo.keywords || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            seo: { ...formData.seo, keywords: e.target.value }
                          })
                        }
                        placeholder="e.g. water purification Kenya, commercial reverse osmosis, Nairobi, milk ATM, Saints Tech"
                        className="w-full text-xs px-3.5 py-2 bg-slate-900 border border-slate-750 rounded-xl text-white"
                      />
                    </div>
                  </div>
                )}

                {/* Bottom Modal Actions */}
                <div className="pt-4 border-t border-slate-800 flex items-center justify-between">
                  <div className="text-[11px] text-slate-400">
                    Saints Tech Solutions • Certified Local Fabrication
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 text-xs font-semibold text-slate-300 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-xl shadow-sm transition-colors cursor-pointer flex items-center gap-1.5"
                    >
                      <Check className="w-4 h-4" />
                      <span>{isEditing ? 'Save Changes' : 'Create & Publish Machine'}</span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ======================================================== */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs">
          <div className="bg-slate-950 rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4 border border-slate-800">
            <div className="w-10 h-10 rounded-full bg-rose-950 text-rose-400 flex items-center justify-center mx-auto border border-rose-800/80">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="text-center space-y-1">
              <h3 className="text-base font-bold text-white">Delete Equipment?</h3>
              <p className="text-xs text-slate-400">
                Are you sure you want to remove this equipment from the catalog? This will update your live storefront catalog.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2 text-xs font-semibold text-slate-300 bg-slate-900 hover:bg-slate-800 rounded-lg border border-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteProduct(deleteConfirmId);
                  setDeleteConfirmId(null);
                }}
                className="flex-1 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-500 rounded-lg"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* RESET CATALOG CONFIRMATION MODAL */}
      {/* ======================================================== */}
      {resetCatalogConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs">
          <div className="bg-slate-950 rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4 border border-slate-800">
            <div className="w-10 h-10 rounded-full bg-amber-950 text-amber-400 flex items-center justify-center mx-auto border border-amber-800/80">
              <RefreshCw className="w-5 h-5" />
            </div>
            <div className="text-center space-y-1">
              <h3 className="text-base font-bold text-white">Reset Catalog to Factory Defaults?</h3>
              <p className="text-xs text-slate-400">
                This will reset your catalog back to the original 14 baseline equipment items (Water RO, Milk ATMs, Cooking Oil ATMs, Pasteurizers).
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setResetCatalogConfirm(false)}
                className="flex-1 py-2 text-xs font-semibold text-slate-300 bg-slate-900 hover:bg-slate-800 rounded-lg border border-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  resetToDefaultProducts();
                  setResetCatalogConfirm(false);
                }}
                className="flex-1 py-2 text-xs font-semibold text-white bg-sky-600 hover:bg-sky-500 rounded-lg"
              >
                Confirm Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* LEAD INQUIRY DETAIL MODAL */}
      {/* ======================================================== */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs">
          <div className="bg-slate-950 rounded-3xl max-w-lg w-full p-6 shadow-2xl space-y-5 border border-slate-800">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <span className="text-[10px] font-mono font-bold text-sky-400">{selectedLead.id}</span>
                <h3 className="text-base font-bold text-white mt-0.5">{selectedLead.customerName}</h3>
                <div className="text-xs text-slate-400">{selectedLead.businessName} • {selectedLead.county}, Kenya</div>
              </div>
              <button
                onClick={() => setSelectedLead(null)}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-900"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 space-y-1">
                <div className="font-bold text-slate-300">Contact Details:</div>
                <div className="flex items-center gap-4 text-slate-200">
                  <span>Phone: <strong className="text-white">{selectedLead.phone}</strong></span>
                  <span>Email: <strong className="text-white">{selectedLead.email}</strong></span>
                </div>
              </div>

              {selectedLead.notes && (
                <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 space-y-1">
                  <div className="font-bold text-slate-300">Customer Project Notes:</div>
                  <p className="text-slate-400 italic">"{selectedLead.notes}"</p>
                </div>
              )}

              <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 space-y-2">
                <div className="font-bold text-slate-300">Inquired Equipment:</div>
                <div className="space-y-1.5">
                  {(selectedLead.items || []).map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between text-slate-300">
                      <span>{it.quantity}x {it.name}</span>
                      <span className="font-bold text-white tabular-nums">KSh {formatKsh(it.price * it.quantity)}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-slate-800 flex items-center justify-between font-bold text-sm text-sky-400">
                    <span>Total Estimate:</span>
                    <span className="tabular-nums">KSh {formatKsh(selectedLead.totalKsh)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <a
                href={`tel:${selectedLead.phone.replace(/[^0-9+]/g, '')}`}
                className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-750 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 border border-slate-700"
              >
                <Phone className="w-3.5 h-3.5 text-emerald-400" />
                <span>Call Client</span>
              </a>
              <a
                href={`https://wa.me/${selectedLead.phone.replace(/[^0-9]/g, '')}?text=Hello%20${encodeURIComponent(
                  selectedLead.customerName
                )},%20this%20is%20Saints%20Tech%20Solutions%20regarding%20quotation%20${selectedLead.id}.`}
                target="_blank"
                rel="noreferrer"
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/30"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
